import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appelApi } from './api';

export type StatutRelation =
  | 'A_PROSPECTER'
  | 'NEGOCIATION'
  | 'PARTENAIRE'
  | 'PARTENAIRE_INSTITUTIONNEL'
  | 'INACTIVE';

export type StatutBesoin = 'OUVERT' | 'POURVU' | 'ABANDONNE';

/**
 * Case « employeur privé / employeur public » du CERFA. Le secteur public
 * entraîne l'adhésion de l'apprenti au régime spécifique d'assurance chômage :
 * ce n'est pas un libellé décoratif.
 */
export type SecteurEmployeur = 'PRIVE' | 'PUBLIC';

/** Types de contrat proposés par la fiche de recueil du besoin. */
export type TypeContratBesoin = 'APPRENTISSAGE' | 'PROFESSIONNALISATION';

/** Une ligne du tableau « description de l'offre » de la fiche papier. */
export interface LigneOffre {
  rncp: string;
  taches: string;
}

export interface Besoin {
  id: string;
  nombrePostes: number;
  formation: string | null;
  formationCode: string | null;
  dateSouhaitee: string | null;
  profilRecherche: string | null;
  ouvertRqth: boolean;
  dateDemande: string | null;
  commercial: string | null;
  typeContrat: TypeContratBesoin | null;
  typeContratLibelle: string | null;
  tachesOffre: LigneOffre[];
  faisabilite: boolean | null;
  motifFaisabilite: string | null;
  candidatNom: string | null;
  candidatTelephone: string | null;
  candidatEmail: string | null;
  statut: StatutBesoin;
  statutLibelle: string;
  statutTon: string;
  creeLe: string;
}

/** Maître d'apprentissage mémorisé dans l'entreprise (fiche `Tuteur`). */
export interface MaitreApprentissageLigne {
  id: string;
  nom: string;
  prenom: string;
  emploiOccupe: string | null;
  telephone: string | null;
  email: string | null;
}

/**
 * Nouvelle fiche de maître d'apprentissage : la même saisie depuis la fiche
 * entreprise et depuis le formulaire du contrat. Seule l'identité est exigée.
 */
export interface NouveauMaitreApprentissage {
  nom: string;
  prenom: string;
  emploiOccupe?: string;
  telephone?: string;
  email?: string;
  anneesExperience?: number;
}

export interface LigneEntreprise {
  id: string;
  siret: string;
  denomination: string;
  secteur: string | null;
  sites: string[];
  statutRelation: StatutRelation;
  statutLibelle: string;
  statutTon: string;
  contact: string | null;
  telephone: string | null;
  email: string | null;
  opco: string | null;
  gerant: string | null;
  contactOperationnel: string | null;
  adresse: string | null;
  codePostal: string | null;
  ville: string | null;
  idcc: string | null;
  effectif: number | null;
  siteWeb: string | null;
  numeroVoie: string | null;
  voie: string | null;
  complementAdresse: string | null;
  secteurEmployeur: SecteurEmployeur | null;
  typeEmployeur: string | null;
  employeurSpecifique: string | null;
  caisseRetraite: string | null;
  codeNaf: string | null;
  postesOuverts: number;
  besoins: Besoin[];
  maitresApprentissage: MaitreApprentissageLigne[];
  apprenantsAccueillis: number;
  dernierContactLe: string | null;
  relanceAttendue: boolean;
}

export interface ReponseEntreprises {
  entreprises: LigneEntreprise[];
  total: number;
  secteursSuggeres: string[];
  typesEmployeurSuggeres: string[];
  employeursSpecifiquesSuggeres: string[];
  statuts: { valeur: StatutRelation; libelle: string; ton: string }[];
  formations: { code: string; titre: string }[];
  typesContrat: { valeur: TypeContratBesoin; libelle: string }[];
  compteurs: {
    partenaires: number;
    postesOuverts: number;
    enNegociation: number;
    aProspecter: number;
    relancesAttendues: number;
  };
}

/**
 * Filtre sur les besoins de l'entreprise, côté serveur.
 *
 * `OUVERT` postes à pourvoir ; `RQTH` postes ouverts aux profils RQTH ;
 * `CLOS` tous les besoins clos ; `AUCUN` aucun besoin recueilli.
 */
export type FiltreBesoin = 'OUVERT' | 'RQTH' | 'CLOS' | 'AUCUN';

export const LIBELLES_FILTRE_BESOIN: { valeur: FiltreBesoin; libelle: string }[] = [
  { valeur: 'OUVERT', libelle: 'Avec postes à pourvoir' },
  { valeur: 'RQTH', libelle: 'Postes ouverts RQTH' },
  { valeur: 'CLOS', libelle: 'Besoins tous clos' },
  { valeur: 'AUCUN', libelle: 'Sans besoin recueilli' },
];

export interface FiltresEntreprises {
  site: string;
  statut?: StatutRelation;
  recherche?: string;
  besoin?: FiltreBesoin;
}

export function useEntreprises(filtres: FiltresEntreprises) {
  const parametres = new URLSearchParams({ site: filtres.site });
  if (filtres.statut) parametres.set('statut', filtres.statut);
  if (filtres.recherche) parametres.set('recherche', filtres.recherche);
  if (filtres.besoin) parametres.set('besoin', filtres.besoin);

  return useQuery({
    queryKey: ['entreprises', parametres.toString()],
    queryFn: () => appelApi<ReponseEntreprises>(`/entreprises?${parametres.toString()}`),
  });
}

function invalider(client: ReturnType<typeof useQueryClient>): void {
  void client.invalidateQueries({ queryKey: ['entreprises'] });
}

export interface EntreeEntreprise {
  siret: string;
  denomination: string;
  sites: string[];
  secteur?: string;
  statutRelation?: StatutRelation;
  gerant?: string;
  contactOperationnel?: string;
  email?: string;
  telephone?: string;
  ville?: string;
  codePostal?: string;
  opco?: string;
  idcc?: string;
  effectif?: number;
  adresse?: string;
  siteWeb?: string;
  secteurEmployeur?: SecteurEmployeur;
  typeEmployeur?: string;
  employeurSpecifique?: string;
  caisseRetraite?: string;
  codeNaf?: string;
  numeroVoie?: string;
  voie?: string;
  complementAdresse?: string;
  maitresApprentissage?: NouveauMaitreApprentissage[];
}

export function useCreerEntreprise() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (entree: EntreeEntreprise) =>
      appelApi<LigneEntreprise>('/entreprises', { methode: 'POST', corps: entree }),
    onSuccess: () => invalider(client),
  });
}

/**
 * Correction de la fiche entreprise : tout ce qui se saisit à la création se
 * corrige, SIRET compris. Un champ envoyé vide est effacé côté API.
 */
export interface ModificationEntreprise {
  statutRelation?: StatutRelation;
  siret?: string;
  denomination?: string;
  secteur?: string;
  gerant?: string;
  contactOperationnel?: string;
  email?: string;
  telephone?: string;
  adresse?: string;
  codePostal?: string;
  ville?: string;
  opco?: string;
  idcc?: string;
  effectif?: number | null;
  siteWeb?: string;
  secteurEmployeur?: SecteurEmployeur | null;
  typeEmployeur?: string;
  employeurSpecifique?: string;
  caisseRetraite?: string;
  codeNaf?: string;
  numeroVoie?: string;
  voie?: string;
  complementAdresse?: string;
  sites?: string[];
  contactPris?: boolean;
  /** Fiches à **ajouter** : les existantes ne se corrigent pas ici. */
  maitresApprentissage?: NouveauMaitreApprentissage[];
}

export function useModifierEntreprise() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: { id: string } & ModificationEntreprise) => {
      const { id, ...corps } = variables;
      return appelApi<LigneEntreprise>(`/entreprises/${id}`, { methode: 'PATCH', corps });
    },
    onSuccess: () => invalider(client),
  });
}

export interface EntreeBesoin {
  nombrePostes: number;
  formationCode?: string;
  dateSouhaitee?: string;
  profilRecherche?: string;
  ouvertRqth?: boolean;
  dateDemande?: string;
  commercial?: string;
  typeContrat?: TypeContratBesoin;
  tachesOffre?: LigneOffre[];
  faisabilite?: boolean;
  motifFaisabilite?: string;
  candidatNom?: string;
  candidatTelephone?: string;
  candidatEmail?: string;
}

export interface ResultatBesoin {
  entreprise: LigneEntreprise;
  /** Nombre de personnes du pôle commercial transmises pour identifier un candidat. */
  informes: number;
}

export function useEnregistrerBesoin() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: { entrepriseId: string } & EntreeBesoin) => {
      const { entrepriseId, ...corps } = variables;
      return appelApi<ResultatBesoin>(`/entreprises/${entrepriseId}/besoins`, {
        methode: 'POST',
        corps,
      });
    },
    onSuccess: () => invalider(client),
  });
}

/** Modification d'un besoin : contenu, statut, ou les deux. */
export interface ModificationBesoin {
  statut?: 'POURVU' | 'ABANDONNE';
  nombrePostes?: number;
  /** `null` détache la formation du besoin. */
  formationCode?: string | null;
  dateSouhaitee?: string | null;
  profilRecherche?: string | null;
  ouvertRqth?: boolean;
  dateDemande?: string | null;
  commercial?: string | null;
  typeContrat?: TypeContratBesoin | null;
  tachesOffre?: LigneOffre[] | null;
  faisabilite?: boolean | null;
  motifFaisabilite?: string | null;
  candidatNom?: string | null;
  candidatTelephone?: string | null;
  candidatEmail?: string | null;
}

export function useModifierBesoin() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: { besoinId: string } & ModificationBesoin) => {
      const { besoinId, ...corps } = variables;
      return appelApi<LigneEntreprise>(`/entreprises/besoins/${besoinId}`, {
        methode: 'PATCH',
        corps,
      });
    },
    onSuccess: () => invalider(client),
  });
}

export function useSupprimerBesoin() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (besoinId: string) =>
      appelApi<LigneEntreprise>(`/entreprises/besoins/${besoinId}`, { methode: 'DELETE' }),
    onSuccess: () => invalider(client),
  });
}

/**
 * Adresse de la fiche de recueil en PDF. Le jeton de session voyage dans un
 * cookie : le navigateur ouvre cette adresse directement, sans `fetch` ni
 * `Blob` à gérer pour une simple prévisualisation.
 */
export function urlFicheBesoin(besoinId: string): string {
  const base = import.meta.env.VITE_API_BASE ?? '/api';
  return `${base}/entreprises/besoins/${besoinId}/fiche`;
}
